import { use, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { data, LoaderFunctionArgs, useFetcher } from "react-router";
import invariant from "tiny-invariant";

import {
  DownloadAttestation,
  DownloadFootprintAnalysis,
} from "@/components/Communicate";
import { UserContext } from "@/contexts";
import { TReviewCommunicateResponse, TTrackingCampaign } from "@/types";

export async function loader({ params }: LoaderFunctionArgs) {
  const { reviewId } = params;

  invariant(reviewId, "No review id found!");

  const reviewCommunicateResponse = await fetch(
    `/api/client/reviews/${reviewId}/communicate`
  );

  if (reviewCommunicateResponse.status !== 200) {
    throw data("Failed to load the review communication data", {
      status: reviewCommunicateResponse.status,
    });
  }

  const { data: reviewCommunicateData } =
    (await reviewCommunicateResponse.json()) as {
      data: TReviewCommunicateResponse;
    };

  return { reviewCommunicateData };
}

const toDate = (date: string) => date.slice(0, 10);

function AnalysisAndCommunication({
  trackingCampaigns,
  periodStart,
  periodEnd,
  companyName,
}: {
  trackingCampaigns: TTrackingCampaign[];
  periodStart: string;
  periodEnd: string;
  companyName: string;
}) {
  const { t } = useTranslation(["dashboard"]);
  const { user } = use(UserContext);
  const fetcher = useFetcher<typeof loader>();

  // Deliverables can only be generated for finished and internal reviews, as on the Communicate page
  const downloadableTrackingCampaigns = trackingCampaigns.filter(
    (trackingCampaign) =>
      trackingCampaign.finished && !trackingCampaign.score.is_external
  );
  // Deliverables must match the period of the consolidated review displayed in the charts
  const selectedReviewId = downloadableTrackingCampaigns.find(
    (trackingCampaign) =>
      toDate(trackingCampaign.period_start) === toDate(periodStart) &&
      toDate(trackingCampaign.period_end) === toDate(periodEnd)
  )?.review_id;
  const isDisabled = !selectedReviewId;

  const loadReviewCommunicateData = (reviewId?: number) => {
    if (reviewId) {
      fetcher.load(`/dashboard/multientity/review-communicate/${reviewId}`);
    }
  };

  useEffect(() => {
    loadReviewCommunicateData(selectedReviewId);
  }, []);

  const reviewCommunicateData = fetcher.data?.reviewCommunicateData;
  const isFreeTrial = user?.company.subscriptionType === "start";
  // Same conditions as on the Communicate page
  const canDownloadFootprintAnalysis =
    reviewCommunicateData?.id === selectedReviewId &&
    (reviewCommunicateData?.calculated_scopes.ghg_2024 || isFreeTrial);

  const deliverablesUnavailableTooltip = t(
    "dashboard:analysisAndCommunication.deliverablesUnavailable",
    { companyName }
  );

  return (
    <div className="gap-y-2xs flex flex-col">
      <div className="gap-x-sm mb-xs flex">
        <DownloadAttestation
          reviewId={selectedReviewId}
          isMultientityDashboard
          disabled={isDisabled}
          disabledTooltip={deliverablesUnavailableTooltip}
        />
        {isDisabled ? (
          <DownloadFootprintAnalysis
            isMultientityDashboard
            disabled
            disabledTooltip={deliverablesUnavailableTooltip}
          />
        ) : canDownloadFootprintAnalysis ? (
          <DownloadFootprintAnalysis
            reviewId={selectedReviewId}
            isMultientityDashboard
          />
        ) : null}
      </div>
    </div>
  );
}

export default AnalysisAndCommunication;
